import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import "@assets/css/bootstrap-grid.css";
import { getInputField } from "@utils/function/index.function";
import { FormField } from "@utils/interface/form.interface";
import {
  UserResetPasswordFormData,
  UserResetPasswordSchema,
} from "@utils/interface/form.schema";
import { usePostData } from "@utils/query/index.query";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useResetPassword, useSelectionStore } from "@utils/states/index.state";
import { useNavigate } from "react-router";
import { ChevronLeft, Eye } from "lucide-react";
import { EyeClosedIcon } from "@assets/icons";
import { METHODS } from "@utils/interface/index.interface";
import { endpoint } from "@services/api.services";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";

const ResetPasswordForm: React.FC = () => {
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<UserResetPasswordFormData>({
    mode: "onChange",
    resolver: zodResolver(UserResetPasswordSchema),
  });

  /* set default value */
  useEffect(() => {}, []);

  const { mutate, isPending } = usePostData();
  const { otp, email } = useResetPassword();
  const navigate = useNavigate();

  const handleMutate = (data: any) => {
    return {
      data,
      endpoint: endpoint["auth"]["resetPassword"],
      method: METHODS.POST,
    };
  };
  const handleSuccess = async ({ data, status }: any) => {
    if (status !== 200) {
      return showToast({
        description: data.details,
        type: "error",
        title: "Error",
      });
    }
    console.log(data, "success");
    showToast({
      description: "Password Changed Successfully",
      type: "success",
      title: "Success",
    });
    navigate("/login");
  };
  const { setIsFormPending } = useSelectionStore();
  const handleError = async (error: any) => {
    setIsFormPending(false);
    throwErrorAlert(error);
  };
  const onSubmit: SubmitHandler<UserResetPasswordFormData> = (data) => {
    if (data.password !== data.cpassword) return console.log("error");
    mutate(
      handleMutate({
        ...data,
        otp: otp,
        email: email,
      }),
      {
        onSuccess: ({ data, status }) => handleSuccess({ data, status }),
        onError: (data) => handleError({ data }),
      }
    );
  };

  const [showPassword, setShowPassword] = useState({
    cPassword: false,
    newPassword: false,
  });
  const formSchema: FormField[] = [
    {
      name: "password",
      label: "New Password",
      colSpan: 12,
      type: showPassword.newPassword ? "text" : "password",
      control: control,
      endIcon: (
        <>
          {showPassword.newPassword ? (
            <Eye
              size={17}
              color="#ABABAB"
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  newPassword: !prevState.newPassword,
                }))
              }
            />
          ) : (
            <span
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  newPassword: !prevState.newPassword,
                }))
              }
            >
              <EyeClosedIcon />
            </span>
          )}
        </>
      ),
      setValue: setValue,
      errors,
      placeholder: "**********",
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "cpassword",
      label: "Confirm Password",
      colSpan: 12,
      placeholder: "**********",
      type: showPassword.cPassword ? "text" : "password",
      control: control,
      endIcon: (
        <>
          {showPassword.cPassword ? (
            <Eye
              size={17}
              color="#ABABAB"
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  cPassword: !prevState.cPassword,
                }))
              }
            />
          ) : (
            <span
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  cPassword: !prevState.cPassword,
                }))
              }
            >
              <EyeClosedIcon />
            </span>
          )}
        </>
      ),
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "submit",
      value: "Reset Password",
      className: "w-full h-[47px] mt-[32px]",
      colSpan: 12,
      variant: "default",
      endIcon: isPending ? <Spinner /> : null,
      disabled: isPending ? true : false,
      visable: true,
    },
    {
      type: "button",
      onClick: () => {
        navigate("/login");
      },
      value: "Back to login ",
      className: "w-full h-[47px] mt-[4px] text-[#1DA077]",
      startIcon: <ChevronLeft />,
      colSpan: 12,

      variant: "ghost",
      disabled: isPending ? true : false,
      visable: true,
    },
  ];

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="row w-[424px] max-md:w-full max-md:p-4"
      >
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default ResetPasswordForm;
